// Base
// Import the WelcomeBack component
// Library
import * as Sentry from "@sentry/browser";
import _, { get, size } from "lodash";
import { usePostHog } from "posthog-js/react";
import queryString from "query-string";
import { useEffect, useRef, useState } from "react";
import { trackPromise } from "react-promise-tracker";
import { usePromiseTracker } from "react-promise-tracker";

import "./App.css";
import "./amplifyClient";
// Components
import IncidentBanner from "./components/IncidentBanner";
import Loader from "./components/Loader";
import Nav from "./components/Nav";
import ReportNav from "./components/report/ReportNav";
import ReportPage from "./components/report/ReportPage";
import { ReportProvider } from "./components/report/ReportProvider";
import { SENTRY_TAG_CLIENT_ID, SENTRY_TAG_IGNITION_TOKEN } from "./constants";
import { checkAuthenticatedUser } from "./lib/authUtility";
import { findBackIndex } from "./lib/backNavigation";
import { getSession, updateIgnition } from "./lib/axle";
import { getClient, getClientWithIgnitionConfig } from "./lib/dmv";
import { identifyPosthogUser } from "./lib/posthog";
import { useReportSurvey } from "./lib/reportSurvey";
import { REPORT_STEP } from "./lib/reports";
import {
  checkIgnitionToken,
  getUserAndMetadataFromQueryParams,
  isIntegrationV123,
} from "./lib/utility";
// Steps (special components)
import AxleAccountSignIn from "./steps/AxlePassport/AxleAccountSignIn";
import AxleAccountSignUp from "./steps/AxlePassport/AxleAccountSignUp";
import EnterOTP from "./steps/AxlePassport/EnterOTP";
import BasicPolicyInfo from "./steps/BasicPolicyInfo";
import Carriers from "./steps/Carriers";
import Confirm from "./steps/Confirm";
import Consent from "./steps/Consent";
import CreditCards from "./steps/CreditCards";
import EnhancedManualPolicyDocument from "./steps/EnhancedManualPolicyDocument";
import EnterMfa from "./steps/EnterMfa";
import Error from "./steps/Error";
import Exit from "./steps/Exit";
import { ExitSurvey } from "./steps/ExitSurvey";
import Failed from "./steps/Failed";
import Interstitial from "./steps/Interstitial";
import Login from "./steps/Login";
import LoginSupport from "./steps/LoginSupport";
import ManualAccount from "./steps/ManualAccount";
import ManualCreditCard from "./steps/ManualCreditCard";
import ManualPolicyDocument from "./steps/ManualPolicyDocument";
import ManualPolicyInfo from "./steps/ManualPolicyInfo";
import Permissions from "./steps/Permissions";
import PolicyLookup from "./steps/PolicyLookup";
import SelectVerificationMethod from "./steps/SelectVerificationMethod";
import SendMfa from "./steps/SendMfa";
import StepTransition from "./steps/StepTransition";
import Success from "./steps/Success";
import UnsupportedCarrier from "./steps/UnsupportedCarrier";
import ValidatePolicy from "./steps/ValidatePolicy";
import WelcomeBack from "./steps/WelcomeBack";

const USER_KEYS = ["id", "firstName", "lastName", "email", "phone"];

function App() {
  const [step, setStep] = useState("");
  const [history, setHistory] = useState([]);
  const [loginInformation, setLoginInformation] = useState({});
  const [accountInfo, setAccountInfo] = useState({});
  const [policyInfo, setPolicyInfo] = useState({});
  const [session, setSession] = useState({});
  const [client, setClient] = useState({});
  const [error, setError] = useState(null);
  const [linkError, setLinkError] = useState("retrieve information");
  const [manualError, setManualError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [manualRetryCount, setManualRetryCount] = useState(0);
  const [carriers, setCarriers] = useState([]);
  const [partnerCarrierConfig, setPartnerCarrierConfig] = useState({});
  const [loginAttempts, setLoginAttempts] = useState({});
  const [serverErrorAttempts, setServerErrorAttempts] = useState({});
  const [linkAttempts, setLinkAttempts] = useState({});
  const [integration, setIntegration] = useState(undefined);
  const [bootLoading, setBootLoading] = useState(true); // Explicit boot loader flag

  /* Axle Passport RUX states */
  const [cognitoAuthTokens, setCognitoAuthTokens] = useState({}); // Store auth tokens returned from Cognito on successful sign in
  const [ruxTokenData, setRuxTokenData] = useState(null); // Stores the Rux account data required for the Welcome Back step for Axle Passport
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(null); // Store the user's authentication status
  const [phoneNumber, setPhoneNumber] = useState(""); // Store the user's phone number
  const [isReturningUserInSignUpFlow, setIsReturningUserInSignUpFlow] =
    useState(false); // Store whether the user is a returning user that is attempting to login in through the sign up flow
  const [showNav, setShowNav] = useState(true);

  // Set up promise tracker
  // NOTE: we use a delay here to debounce quick API calls
  const { promiseInProgress } = usePromiseTracker({ delay: 500 });

  /**
   * Sets the next step
   * @param {*} nextStep
   * @param {*} noHistory - If true, do not add current step to history
   */
  const nextStep = (nextStep, noHistory) => {
    setStep(nextStep);
    // Steps that hide the nav while loading don't restore it on every exit path.
    setShowNav(true);
    if (!noHistory) {
      setHistory([...history, step]);
    }
  };

  const backIndex = findBackIndex(history, step);

  const prevStep = () => {
    setStep(history[backIndex]);
    setShowNav(true);
    setHistory(history.slice(0, backIndex));
  };

  const setCarrier = (carrier) => {
    setLoginInformation({ ...loginInformation, ...carrier });
  };

  const posthog = usePostHog();
  const submitUserReport = useReportSurvey(posthog, {
    integration,
    carrierId: get(loginInformation, "carrier.id"),
    clientId: client.id,
  });

  useEffect(() => {
    // Setup session
    const setup = async () => {
      // Pull all query parameters from browser
      const parsedParams = queryString.parse(window.location.search);
      const { token, client, origin, templateId, ...restOfParams } =
        parsedParams;

      // If no ignitionToken or client provided, throw error
      if (!token && !client) {
        setError("No token or client provided.");
        return;
      }

      try {
        let ignitionToken = token;

        // If no ignitionToken,  run set up for interstitial carriers, and then exit
        if (!token) {
          // Split rest of params by user and metadata
          const { userQueryParams, metadataQueryParams } =
            getUserAndMetadataFromQueryParams(restOfParams, USER_KEYS);
          // Check if clientId is available
          if (!client) {
            setError("No client provided.");
            return;
          }

          const [retrievedClient, retrievedClientError] =
            await getClientWithIgnitionConfig(client, templateId);

          if (retrievedClientError) {
            switch (get(retrievedClientError, "code")) {
              case 400:
                setError("AxleClient does not exist.");
                return;
              case 500:
                console.log(JSON.stringify(retrievedClientError));
                nextStep("axle-error");
                return;
              default:
                nextStep("connection-error");
                return;
            }
          }

          setClient(retrievedClient);

          // Ensure user, metadata, and origin is set in session if provided
          setSession({
            user: userQueryParams,
            metadata: metadataQueryParams,
            origin,
            templateId,
          });
          setStep("interstitial");
          return;
        }

        // Pre-validate ignitionToken
        const ignitionTokenValidation = checkIgnitionToken(ignitionToken);

        if (!ignitionTokenValidation.status) {
          setError(ignitionTokenValidation.message);
          return;
        }

        Sentry.setTag(SENTRY_TAG_IGNITION_TOKEN, ignitionToken);

        // Retrieve session information, can optimize in the future to skip if createIgnition is called
        const [retrievedSession, retrievedSessionError] = await getSession(
          ignitionToken
        );

        if (retrievedSessionError) {
          switch (get(retrievedSessionError, "code")) {
            case 500:
              console.log(JSON.stringify(retrievedSessionError));
              nextStep("axle-error");
              return;
            case 400:
              if (
                get(retrievedSessionError, "message", "")?.includes(
                  "Session already completed."
                )
              ) {
                setError(`Sorry, you cannot reuse a completed session.`);
              } else {
                //message from ign-backend: "Invalid ignition token provided."
                setError("Invalid or expired link.");
              }
              return;
            default:
              nextStep("connection-error");
              return;
          }
        }

        const [retrievedClient, retrievedClientError] = await getClient(
          retrievedSession.client
        );

        Sentry.setTag(SENTRY_TAG_CLIENT_ID, retrievedSession.client);

        if (retrievedClientError) {
          switch (get(retrievedClientError, "code")) {
            case 500:
              console.log(JSON.stringify(retrievedClientError));
              nextStep("axle-error");
              return;
            default:
              nextStep("connection-error");
              return;
          }
        }

        // If session status === "completed"
        if (retrievedSession.status === "completed") {
          setError(
            `Sorry, you cannot reuse a completed session. Please contact ${retrievedClient.displayName} to generate a new session.`
          );
          return;
        } else if (
          isIntegrationV123(
            loginInformation,
            get(retrievedSession, "integration"),
            retrievedSession
          ) &&
          retrievedSession.status === "errored"
        ) {
          setError(`Sorry, you cannot reuse a completed session.`);
          return;
        }

        // Set client to state
        setClient(retrievedClient);

        // Add origin to session
        retrievedSession.origin = origin;

        // Set session to state
        setSession(retrievedSession);

        // Initialize Posthog user
        identifyPosthogUser(posthog, retrievedSession, retrievedClient);

        // Update Ignition with opened event
        const [updateIgnitionResponse, updateIgnitionError] =
          await updateIgnition(retrievedSession.id, {
            status: "opened",
            lastEvent: {
              type: `ignition.opened`,
              data: {
                token: retrievedSession.id,
                user: retrievedSession.user,
                client: retrievedClient.id,
              },
              createdAt: new Date().toISOString(),
            },
          });

        if (updateIgnitionError) {
          switch (get(updateIgnitionError, "code")) {
            case 500:
              console.log(JSON.stringify(updateIgnitionError));
              nextStep("axle-error");
              return;
            default:
              nextStep("connection-error");
              return;
          }
        }

        // Determine if firstStep is interstitial based on config
        const firstStep = _(retrievedSession).get("config.interstitial.enabled")
          ? "interstitial"
          : "consent";

        // Go to first step
        setStep(firstStep);
        return;
      } catch (error) {
        console.error(error);
        setError("Invalid or expired link.");
      }
    };
    trackPromise(
      setup().finally(() => {
        // Ensure boot loader turns off regardless of success/failure
        setBootLoading(false);
      })
    );
  }, []);

  // Run checkAuthenticatedUser only once, after the session is first populated
  const authCheckedRef = useRef(false);

  useEffect(() => {
    // Only proceed if we have a session and we haven't already run the auth check
    if (!session?.id || authCheckedRef.current) return;

    authCheckedRef.current = true; // ensure we don't execute again

    let isMounted = true; // avoid state updates after unmount

    const authCheck = async () => {
      const { isAuthenticated, tokens, ruxTokenData } =
        await checkAuthenticatedUser(session);

      if (!isMounted) return;

      if (isAuthenticated && tokens && ruxTokenData) {
        setCognitoAuthTokens(tokens);
        setIsUserAuthenticated(true);
        setRuxTokenData(ruxTokenData);
      } else {
        setIsUserAuthenticated(false);
      }
    };

    trackPromise(authCheck());

    return () => {
      isMounted = false;
    };
  }, [session]);

  // Show full screen transparent loader only while the initial setup promise is in-flight. If setup completes successfully or with error, loader is removed.
  if (bootLoading) {
    return <Loader isOpen={true} transparent />;
  }

  const Navigation = step === REPORT_STEP ? ReportNav : Nav;

  const app = (
    <div
      // dvh tracks the browser's toolbars; innerHeight on iOS also shrinks with pinch zoom and the keyboard.
      className="flex h-dvh w-full items-center justify-center bg-canvas"
      style={{
        background: get(session, "config.styles.background", undefined),
      }}
    >
      <div
        data-step={step}
        className="relative flex h-full w-full flex-col overflow-hidden bg-surface text-ink md:h-[680px] md:max-h-[calc(100dvh-48px)] md:w-4/6 md:min-w-[420px] lg:w-2/6 md:rounded-lg md:border md:border-subtle"
      >
        <IncidentBanner />
        {step && !["interstitial"].includes(step) && !promiseInProgress && (
          <Navigation
            client={client}
            showActions={step !== "success"}
            showLockup={step !== "consent"}
            prevStep={prevStep}
            nextStep={nextStep}
            showPrevStep={backIndex !== -1 && showNav}
            showExit={
              ![
                "success",
                "exit",
                "exit-survey",
                "exit-confirmation",
                "unsupported-carrier",
                "interstitial",
                "enter-otp",
                "axle-account-sign-up",
                "validate-policy",
              ].includes(step) && showNav
            }
          />
        )}
        <Loader isOpen={promiseInProgress} />
        {error ? (
          <div className="px-6 pt-4">
            <div className="z-20 flex items-center gap-x-2.5 rounded bg-fail-bg px-4 py-3 text-body-lg text-danger-ink">
              {error}
            </div>
          </div>
        ) : (
          (() => {
            switch (step) {
              case REPORT_STEP:
                return <ReportPage />;
              case "interstitial":
                return (
                  <Interstitial
                    nextStep={nextStep}
                    session={session}
                    setSession={setSession}
                    client={client}
                    posthog={posthog}
                    step={step}
                  />
                );
              case "consent":
                return (
                  <Consent
                    nextStep={nextStep}
                    step={step}
                    client={client}
                    posthog={posthog}
                    isUserAuthenticated={isUserAuthenticated}
                  />
                );
              case "permissions":
                return (
                  <Permissions
                    nextStep={nextStep}
                    step={step}
                    client={client}
                    posthog={posthog}
                    session={session}
                  />
                );
              case "welcome-back":
                return (
                  <WelcomeBack
                    nextStep={nextStep}
                    step={step}
                    posthog={posthog}
                    setLoginInformation={setLoginInformation}
                    client={client}
                    session={session}
                    setSession={setSession}
                    ruxTokenData={ruxTokenData}
                    setCognitoAuthTokens={setCognitoAuthTokens}
                    setRuxTokenData={setRuxTokenData}
                    setIsUserAuthenticated={setIsUserAuthenticated}
                    setIntegration={setIntegration}
                  />
                );
              case "axle-account-sign-in":
                return (
                  <AxleAccountSignIn
                    nextStep={nextStep}
                    setShowNav={setShowNav}
                    step={step}
                    posthog={posthog}
                    phoneNumber={phoneNumber}
                    setPhoneNumber={setPhoneNumber}
                  />
                );
              case "axle-account-sign-up":
                return (
                  <AxleAccountSignUp
                    nextStep={nextStep}
                    step={step}
                    posthog={posthog}
                    phoneNumber={phoneNumber}
                    setPhoneNumber={setPhoneNumber}
                    accountInfo={accountInfo}
                    loginInformation={loginInformation}
                    setIsReturningUserInSignUpFlow={
                      setIsReturningUserInSignUpFlow
                    }
                    session={session}
                  />
                );
              case "enter-otp":
                return (
                  <EnterOTP
                    nextStep={nextStep}
                    step={step}
                    posthog={posthog}
                    phoneNumber={phoneNumber}
                    setCognitoAuthTokens={setCognitoAuthTokens}
                    setRuxTokenData={setRuxTokenData}
                    setIsUserAuthenticated={setIsUserAuthenticated}
                    previousStep={history[size(history) - 1]}
                    isReturningUserInSignUpFlow={isReturningUserInSignUpFlow}
                    accountInfo={accountInfo}
                    setIsReturningUserInSignUpFlow={
                      setIsReturningUserInSignUpFlow
                    }
                    session={session}
                  />
                );
              case "credit-cards":
                return (
                  <CreditCards
                    step={step}
                    setLoginInformation={setLoginInformation}
                    nextStep={nextStep}
                    session={session}
                    posthog={posthog}
                  />
                );
              case "carriers":
                return (
                  <Carriers
                    step={step}
                    nextStep={nextStep}
                    setCarrier={setCarrier}
                    session={session}
                    posthog={posthog}
                    setCarriers={setCarriers}
                    setIntegration={setIntegration}
                  />
                );
              case "login":
                return (
                  <Login
                    step={step}
                    nextStep={nextStep}
                    setAccountInfo={setAccountInfo}
                    loginInformation={loginInformation}
                    setLoginInformation={setLoginInformation}
                    session={session}
                    previousStep={history[size(history) - 1]}
                    setLinkError={setLinkError}
                    posthog={posthog}
                    loginAttempts={loginAttempts}
                    setLoginAttempts={setLoginAttempts}
                    serverErrorAttempts={serverErrorAttempts}
                    setServerErrorAttempts={setServerErrorAttempts}
                    setShowNav={setShowNav}
                    cognitoAuthTokens={cognitoAuthTokens}
                    isUserAuthenticated={isUserAuthenticated}
                    integration={integration}
                    client={client}
                  />
                );
              case "select-verification-method":
                return (
                  <SelectVerificationMethod
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    posthog={posthog}
                    session={session}
                    setIntegration={setIntegration}
                  />
                );
              case "policy-lookup":
                return (
                  <PolicyLookup
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    setLoginInformation={setLoginInformation}
                    posthog={posthog}
                    session={session}
                    setShowNav={setShowNav}
                    setLinkError={setLinkError}
                    setAccountInfo={setAccountInfo}
                    setPolicyInfo={setPolicyInfo}
                    linkAttempts={linkAttempts}
                    setLinkAttempts={setLinkAttempts}
                    client={client}
                  />
                );

              case "login-support":
                return (
                  <LoginSupport
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    posthog={posthog}
                    setLoginInformation={setLoginInformation}
                    setLoginAttempts={setLoginAttempts}
                    history={history}
                    setHistory={setHistory}
                    integration={integration}
                    previousStep={history[size(history) - 1]}
                  />
                );
              case "confirm":
                return (
                  <Confirm
                    step={step}
                    nextStep={nextStep}
                    accountInfo={accountInfo}
                    loginInformation={loginInformation}
                    carriers={carriers}
                    setPartnerCarrierConfig={setPartnerCarrierConfig}
                    session={session}
                    setPolicyInfo={setPolicyInfo}
                    setLoginInformation={setLoginInformation}
                    setLinkError={setLinkError}
                    posthog={posthog}
                    setShowNav={setShowNav}
                    isUserAuthenticated={isUserAuthenticated}
                    integration={integration}
                  />
                );
              case "validate-policy":
                return (
                  <ValidatePolicy
                    step={step}
                    nextStep={nextStep}
                    session={session}
                    posthog={posthog}
                    policyInfo={policyInfo}
                    client={client}
                    loginInformation={loginInformation}
                  />
                );
              case "success":
                return (
                  <Success
                    accountInfo={accountInfo}
                    session={session}
                    client={client}
                    loginInformation={loginInformation}
                    policyInfo={policyInfo}
                    posthog={posthog}
                    step={step}
                    nextStep={nextStep}
                  />
                );
              case "sendMfa":
                return (
                  <SendMfa
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    setLoginInformation={setLoginInformation}
                    setAccountInfo={setAccountInfo}
                    session={session}
                    setLinkError={setLinkError}
                    posthog={posthog}
                    setShowNav={setShowNav}
                    integration={integration}
                  />
                );
              case "enterMfa":
                return (
                  <EnterMfa
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    setAccountInfo={setAccountInfo}
                    session={session}
                    setLinkError={setLinkError}
                    posthog={posthog}
                    setShowNav={setShowNav}
                    client={client}
                    integration={integration}
                  />
                );
              case "unsupported-carrier":
                return (
                  <UnsupportedCarrier
                    session={session}
                    client={client}
                    loginInformation={loginInformation}
                    posthog={posthog}
                    step={step}
                    nextStep={nextStep}
                  />
                );
              case "basic-policy-info":
                return (
                  <BasicPolicyInfo
                    loginInformation={loginInformation}
                    setAccountInfo={setAccountInfo}
                    setPolicyInfo={setPolicyInfo}
                    nextStep={nextStep}
                    session={session}
                    posthog={posthog}
                    step={step}
                  />
                );
              case "manual-credit-card":
                return (
                  <ManualCreditCard
                    loginInformation={loginInformation}
                    setLoginInformation={setLoginInformation}
                    setAccountInfo={setAccountInfo}
                    setPolicyInfo={setPolicyInfo}
                    nextStep={nextStep}
                    session={session}
                    posthog={posthog}
                    step={step}
                  />
                );
              case "manual-account":
                return (
                  <ManualAccount
                    loginInformation={loginInformation}
                    setLoginInformation={setLoginInformation}
                    accountInfo={accountInfo}
                    setAccountInfo={setAccountInfo}
                    nextStep={nextStep}
                    session={session}
                    history={history}
                    setHistory={setHistory}
                    posthog={posthog}
                    step={step}
                  />
                );
              case "manual-policy-info":
                return (
                  <ManualPolicyInfo
                    loginInformation={loginInformation}
                    accountInfo={accountInfo}
                    policyInfo={policyInfo}
                    setPolicyInfo={setPolicyInfo}
                    nextStep={nextStep}
                    session={session}
                    posthog={posthog}
                    step={step}
                  />
                );
              case "manual-policy-document":
                return (
                  <ManualPolicyDocument
                    integration={integration}
                    loginInformation={loginInformation}
                    setLoginInformation={setLoginInformation}
                    accountInfo={accountInfo}
                    setAccountInfo={setAccountInfo}
                    policyInfo={policyInfo}
                    setPolicyInfo={setPolicyInfo}
                    nextStep={nextStep}
                    session={session}
                    posthog={posthog}
                    step={step}
                    setManualError={setManualError}
                    setShowNav={setShowNav}
                    client={client}
                  />
                );
              case "enhanced-manual-policy-document":
                return (
                  <EnhancedManualPolicyDocument
                    integration={integration}
                    loginInformation={loginInformation}
                    setLoginInformation={setLoginInformation}
                    accountInfo={accountInfo}
                    setAccountInfo={setAccountInfo}
                    policyInfo={policyInfo}
                    setPolicyInfo={setPolicyInfo}
                    manualRetryCount={manualRetryCount}
                    setManualRetryCount={setManualRetryCount}
                    nextStep={nextStep}
                    session={session}
                    posthog={posthog}
                    step={step}
                    setManualError={setManualError}
                    setShowNav={setShowNav}
                    client={client}
                  />
                );
              case "no-policies":
                return (
                  <StepTransition
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    client={client}
                    posthog={posthog}
                  />
                );
              case "exit-confirmation":
                return (
                  <StepTransition
                    step={step}
                    session={session}
                    client={client}
                    loginInformation={loginInformation}
                    nextStep={nextStep}
                    previousStep={history[size(history) - 1]}
                    posthog={posthog}
                  />
                );
              case "exit":
                return (
                  <Exit
                    session={session}
                    client={client}
                    previousStep={history[size(history) - 1]}
                    history={history}
                    posthog={posthog}
                    step={step}
                    nextStep={nextStep}
                  />
                );
              case "exit-survey":
                return (
                  <ExitSurvey
                    session={session}
                    step={step}
                    nextStep={nextStep}
                    posthog={posthog}
                  />
                );
              case "failed":
                return (
                  <Failed
                    session={session}
                    client={client}
                    previousStep={history[size(history) - 1]}
                    linkError={linkError}
                    manualError={manualError}
                    posthog={posthog}
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    integration={integration}
                    setShowNav={setShowNav}
                  />
                );
              case "carrier-error":
                return (
                  <Error
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    previousStep={history[size(history) - 1]}
                    client={client}
                    linkError={linkError}
                    posthog={posthog}
                  />
                );
              case "mfa-options-error":
                return (
                  <Error
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    previousStep={history[size(history) - 1]}
                    client={client}
                    linkError={linkError}
                    posthog={posthog}
                  />
                );
              case "manual-document-error":
                return (
                  <Error
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    previousStep={history[size(history) - 1]}
                    client={client}
                    manualError={manualError}
                    posthog={posthog}
                  />
                );
              case "connection-error":
                return (
                  <Error
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    previousStep={history[size(history) - 1]}
                    client={client}
                    manualError={manualError}
                    posthog={posthog}
                  />
                );
              case "axle-error":
                return (
                  <Error
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    previousStep={history[size(history) - 1]}
                    client={client}
                    manualError={manualError}
                    posthog={posthog}
                  />
                );

              /* ===== Enhanced Manual Errors Start ===== */
              case "unsupported-document":
                return (
                  <StepTransition
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    previousStep={history[size(history) - 1]}
                    client={client}
                    posthog={posthog}
                  />
                );
              case "document-missing-fields":
                return (
                  <StepTransition
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    previousStep={history[size(history) - 1]}
                    client={client}
                    posthog={posthog}
                  />
                );
              case "document-type-not-supported":
                return (
                  <StepTransition
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    previousStep={history[size(history) - 1]}
                    client={client}
                    posthog={posthog}
                  />
                );
              case "text-validation-failed":
                return (
                  <StepTransition
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    previousStep={history[size(history) - 1]}
                    client={client}
                    posthog={posthog}
                  />
                );

              /* ===== Enhanced Manual Errors End ===== */
              case "backup-document-required":
                return (
                  <StepTransition
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    previousStep={history[size(history) - 1]}
                    client={client}
                    posthog={posthog}
                  />
                );
              case "carrier-maintenance":
                return (
                  <StepTransition
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    previousStep={history[size(history) - 1]}
                    client={client}
                    posthog={posthog}
                  />
                );
              case "declaration-page-sample":
                return (
                  <StepTransition
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    previousStep={history[size(history) - 1]}
                    client={client}
                    posthog={posthog}
                  />
                );
              case "id-card-sample":
                return (
                  <StepTransition
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    previousStep={history[size(history) - 1]}
                    client={client}
                    posthog={posthog}
                  />
                );
              case "aaa-chapter-error":
                return (
                  <StepTransition
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    previousStep={history[size(history) - 1]}
                    client={client}
                    posthog={posthog}
                  />
                );
              case "account-pending":
              case "policy-pending":
              case "policy-not-active":
              case "mfa-enrollment-required":
                return (
                  <StepTransition
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    previousStep={history[size(history) - 1]}
                    client={client}
                    posthog={posthog}
                  />
                );

              case "session-expired":
                return (
                  <StepTransition
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    previousStep={history[size(history) - 1]}
                    client={client}
                    retryCount={retryCount}
                    setRetryCount={setRetryCount}
                    posthog={posthog}
                  />
                );
              case "max-login-attempts":
              case "max-server-login-attempts":
              case "max-link-attempts":
                return (
                  <StepTransition
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    client={client}
                    previousStep={history[size(history) - 1]}
                    posthog={posthog}
                    integration={integration}
                  />
                );
              case "redirect-login":
                return (
                  <StepTransition
                    step={step}
                    nextStep={nextStep}
                    loginInformation={loginInformation}
                    session={session}
                    client={client}
                    previousStep={history[size(history) - 1]}
                    posthog={posthog}
                    setLoginInformation={setLoginInformation}
                    history={history}
                    setHistory={setHistory}
                    partnerCarrierConfig={partnerCarrierConfig}
                    setIntegration={setIntegration}
                  />
                );
              default:
                return;
              // do nothing
            }
          })()
        )}
        {}
      </div>
    </div>
  );

  return (
    <ReportProvider
      sessionId={session.id}
      step={step}
      goTo={setStep}
      submitReport={submitUserReport}
    >
      {app}
    </ReportProvider>
  );
}

export default App;
